import {Injectable} from '@angular/core';
import {Actions, Effect, ofType} from '@ngrx/effects';
import {catchError, mergeMap, switchMap, take} from 'rxjs/operators';
import {from, of} from 'rxjs';
import {LoadError} from '@app/core/store/actions/error.actions';
import {
	AddShiftStockBid,
	AddShiftStockBidSuccess,
	AddShiftToStock,
	AddShiftToStockSuccess,
	DeleteShift,
	DeleteShiftsInInterval,
	DeleteShiftsInIntervalSuccess,
	DeleteShiftSuccess,
	EScheduleActions,
	GetAvailabilities,
	GetAvailabilitiesSuccess,
	GetGroups,
	GetGroupsSuccess,
	GetShift,
	GetShifts,
	GetShiftsSuccess,
	GetShiftSuccess,
	GetUsers,
	GetUserShifts,
	GetUserShiftsSuccess,
	GetUsersSuccess,
	PatchShift,
	PatchShiftSuccess,
	PostShift,
	PostShiftSuccess,
	RemoveShiftFromStock,
	RemoveShiftFromStockSuccess,
	RemoveShiftStockBid,
	RemoveShiftStockBidSuccess,
	SetAvailableUsers,
	SetFetchedShift,
	SetSelectedDate,
	SetSelectedShift,
	SetSortingType,
	SetUsersWhoBid,
	SetViewType,
} from '@app/core/store/actions/schedule.actions';
import {IGetShiftResponse, IGetShiftsResponse, ShiftService} from '@app/modules/shared/services/shift.service';
import {Group} from '@app/core/models/group.model';
import {User} from '@app/core/models/user.model';
import {UserService} from '@app/modules/shared/services/user.service';
import {GroupService} from '@app/modules/shared/services/group.service';
import {MatDialog} from '@angular/material/dialog';
import {Shift} from '@app/core/models/shift.model';
import {Availability} from '@app/core/models/availability.model';
import {AvailabilityService} from '@app/modules/shared/services/availability.service';
import {IAppState} from '@app/core/store/state/app.state';
import {Store} from '@ngrx/store';
import {getCurrentState} from '@app/core/helpers';
import {ShiftPopupComponent} from '@app/core/popups/shift-popup/shift-popup.component';
import {SnackBar} from '@app/core/services/snackbar.service';

@Injectable()
export class ScheduleEffects {

	@Effect()
	public onGetShifts$ = this.actions$.pipe(
		ofType<GetShifts>(EScheduleActions.GetShifts),
		switchMap((action: GetShifts) => from(this.shiftService.getShifts({
			start_date: action.startDate.format('YYYY-MM-DD'),
			end_date: action.endDate.format('YYYY-MM-DD'),
		}, getCurrentState(this.store).shiftDrafts.selectedShiftDraft)).pipe(
			switchMap((shifts: IGetShiftsResponse) => [
				new GetShiftsSuccess(shifts.shifts),
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onGetUserShifts$ = this.actions$.pipe(
		ofType<GetUserShifts>(EScheduleActions.GetUserShifts),
		switchMap((action: GetUserShifts) => from(this.shiftService.getUserShifts(action.userId)).pipe(
			switchMap((shifts: Shift[]) => [
				new GetUserShiftsSuccess(shifts),
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onPatchShift$ = this.actions$.pipe(
		ofType<PatchShift>(EScheduleActions.PatchShift),
		mergeMap((action: PatchShift) => from(this.shiftService.patchShift(action.id, action.payload, getCurrentState(this.store).shiftDrafts.selectedShiftDraft)).pipe(
			mergeMap((shift: Shift) => {
				this.dialog.getDialogById('shiftPopupDlg')?.close({cancelled: false});

				return [
					new PatchShiftSuccess(shift, action.id),
				];
			}),
			catchError(error => of(new LoadError(error, action))),
		)),
	);
	@Effect()
	public onPostShift$ = this.actions$.pipe(
		ofType<PostShift>(EScheduleActions.PostShift),
		mergeMap((action: PostShift) => from(this.shiftService.postShift(action.payload, getCurrentState(this.store).shiftDrafts.selectedShiftDraft)).pipe(
			mergeMap((shift: Shift) => {
				this.dialog.getDialogById('shiftPopupDlg')?.close({cancelled: false});

				return [
					new PostShiftSuccess(shift),
				];
			}),
			catchError(error => of(new LoadError(error, action))),
		)),
	);
	@Effect()
	public onDeleteShift$ = this.actions$.pipe(
		ofType<DeleteShift>(EScheduleActions.DeleteShift),
		mergeMap((action: DeleteShift) => from(this.shiftService.deleteShift(action.shift, getCurrentState(this.store).shiftDrafts.selectedShiftDraft)).pipe(
			mergeMap(() => {
				this.dialog.getDialogById('shiftPopupDlg')?.close({cancelled: false});

				return [
					new DeleteShiftSuccess(action.shift),
				];
			}),
			catchError(error => of(new LoadError(error, action))),
		)),
	);
	@Effect()
	public onGetUsers$ = this.actions$.pipe(
		ofType<GetUsers>(EScheduleActions.GetUsers),
		switchMap((action: GetUsers) => from(this.userService.getUsers({
			'filter[departments.id]': action.departmentId,
		})).pipe(
			switchMap((users: User[]) => {
					users.unshift(this.userService.getAvailableUser());

					return [
						new GetUsersSuccess(users),
					];
				}
			),
			catchError(error => of(new LoadError(error, action))),
		)),
	);
	@Effect()
	public onGetGroups$ = this.actions$.pipe(
		ofType<GetGroups>(EScheduleActions.GetGroups),
		switchMap((action: GetGroups) => from(this.groupService.getGroups({
			'filter[department.id]': action.departmentId,
			include: 'users'
		})).pipe(
			switchMap((groups: Group[]) => [
				new GetGroupsSuccess(groups),
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);
	@Effect()
	public onGetAvailabilities$ = this.actions$.pipe(
		ofType<GetAvailabilities>(EScheduleActions.GetAvailabilities),
		switchMap((action: GetAvailabilities) => from(this.availabilityService.getAvailabilitiesInInterval({
			start_date: action.startDate,
			end_date: action.endDate,
		})).pipe(
			switchMap((availabilities: Availability[]) => [
				new GetAvailabilitiesSuccess(availabilities),
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);
	@Effect()
	public onSetSelectedShift$ = this.actions$.pipe(
		ofType<SetSelectedShift>(EScheduleActions.SetSelectedShift),
		switchMap((action: SetSelectedShift) => {
			if (action.shift !== undefined) {
				if (getCurrentState(this.store).schedule.groups.length === 0) {
					this.snackBar.newError('SCHEDULE.NO_GROUPS_IN_DEPARTMENT');

					return [];
				}
				if (!this.dialog.getDialogById('shiftPopupDlg')) {
					this.dialog.open(ShiftPopupComponent, {
						id: 'shiftPopupDlg',
						panelClass: 'fullscreen-dialog-max-s',
					}).afterClosed().pipe(take(1)).subscribe(() => {
						this.store.dispatch(new SetSelectedDate(null));
						this.store.dispatch(new SetUsersWhoBid([]));
						this.store.dispatch(new SetAvailableUsers([]));
						this.store.dispatch(new SetFetchedShift(null));
					});
				}
			}

			return [
				new SetSelectedDate(action.selectedDate),
			];
		}),
	);
	@Effect()
	public onSetSortingType$ = this.actions$.pipe(
		ofType<SetSortingType>(EScheduleActions.SetSortingType),
		switchMap((action: SetSortingType) => {
			localStorage.setItem('sortingType', action.sortingType + '');

			return [];
		}),
	);

	@Effect()
	public onSetViewType$ = this.actions$.pipe(
		ofType<SetViewType>(EScheduleActions.SetViewType),
		switchMap((action: SetViewType) => {
			localStorage.setItem('viewType', action.viewType + '');

			return [];
		}),
	);
	@Effect()
	public onAddShiftToStock$ = this.actions$.pipe(
		ofType<AddShiftToStock>(EScheduleActions.AddShiftToStock),
		switchMap((action: AddShiftToStock) => from(this.shiftService.addShiftToStock(action.shiftId)).pipe(
			switchMap((res: { shift_stock_id: number }) => {
				this.dialog.getDialogById('shiftPopupDlg')?.close({cancelled: false});

				return [
					new AddShiftToStockSuccess(action.shiftId, res.shift_stock_id),
				];
			}),
			catchError(error => of(new LoadError(error, action))),
		)),
	);
	@Effect()
	public onRemoveShiftFromStock$ = this.actions$.pipe(
		ofType<RemoveShiftFromStock>(EScheduleActions.RemoveShiftFromStock),
		switchMap((action: RemoveShiftFromStock) => from(this.shiftService.removeShiftFromStock(action.shiftId)).pipe(
			switchMap(() => {
				this.dialog.getDialogById('shiftPopupDlg')?.close({cancelled: false});

				return [
					new RemoveShiftFromStockSuccess(action.shiftId),
				];
			}),
			catchError(error => of(new LoadError(error, action))),
		)),
	);
	@Effect()
	public onAddShiftStockBid$ = this.actions$.pipe(
		ofType<AddShiftStockBid>(EScheduleActions.AddShiftStockBid),
		switchMap((action: AddShiftStockBid) => from(this.shiftService.addShiftStockBid(action.shiftId)).pipe(
			switchMap(() => {
				this.dialog.getDialogById('shiftPopupDlg')?.close({cancelled: false});

				return [
					new AddShiftStockBidSuccess(action.shiftId),
				];
			}),
			catchError(error => of(new LoadError(error, action))),
		)),
	);
	@Effect()
	public onRemoveShiftStockBid$ = this.actions$.pipe(
		ofType<RemoveShiftStockBid>(EScheduleActions.RemoveShiftStockBid),
		switchMap((action: RemoveShiftStockBid) => from(this.shiftService.removeShiftStockBid(action.shiftId)).pipe(
			switchMap(() => {
				this.dialog.getDialogById('shiftPopupDlg')?.close({cancelled: false});

				return [
					new RemoveShiftStockBidSuccess(action.shiftId),
				];
			}),
			catchError(error => of(new LoadError(error, action))),
		)),
	);
	@Effect()
	public onDeleteShiftsInInterval$ = this.actions$.pipe(
		ofType<DeleteShiftsInInterval>(EScheduleActions.DeleteShiftsInInterval),
		switchMap((action: DeleteShiftsInInterval) => from(this.shiftService.deleteInInterval(
			action.start.format('YYYY-MM-DD'),
			action.end.format('YYYY-MM-DD'),
			getCurrentState(this.store).shiftDrafts.selectedShiftDraft,
		)).pipe(
			switchMap(() => [
				new DeleteShiftsInIntervalSuccess(),
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);
	@Effect()
	public onGetShift$ = this.actions$.pipe(
		ofType<GetShift>(EScheduleActions.GetShift),
		switchMap((action: GetShift) => from(this.shiftService.getShift(
			action.id
		)).pipe(
			switchMap((res: IGetShiftResponse) => [
				new GetShiftSuccess(
					res.shift,
					res.available_users,
					res.users_who_bid,
					res.approved_availabilities_user_id,
					res.pending_availabilities_user_id
				),
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	constructor(
		private actions$: Actions,
		private shiftService: ShiftService,
		private userService: UserService,
		private groupService: GroupService,
		private availabilityService: AvailabilityService,
		private dialog: MatDialog,
		private store: Store<IAppState>,
		private snackBar: SnackBar,
	) {
	}
}
